/**
 * El ciclo de vida de una deuda entre personas.
 *
 * ── LO QUE PASA DE VERDAD CUANDO ALGUIEN TE DEBE ────────────────────────────
 *
 * Casi ninguna deuda entre personas termina como empezó. Termina así:
 *
 *   te pagó completo            → se salda, la plata volvió, nadie perdió
 *   te abonó y quedó debiendo   → sigue viva, y toca volver a acordar fecha
 *   te pagó de más              → o te lo dejó (te entró plata), o se lo
 *                                 devuelves (ahora TÚ le debes a él)
 *   le dijiste "déjalo así"     → ahí sí perdiste esa plata. De verdad.
 *   te resignaste a no cobrar   → igual de caro, pero se llama distinto
 *
 * Una app que solo guarda "saldo pendiente" no distingue ninguno de esos
 * cinco casos. Este archivo existe para que sí los distinga, y para que cada
 * uno le haga a tu patrimonio lo que tiene que hacerle.
 *
 * ── LA REGLA QUE LO ORDENA TODO ─────────────────────────────────────────────
 *
 * Prestar y cobrar son TRASLADOS: la plata cambia de sitio, tu patrimonio no
 * se mueve. Perdonar una deuda es un GASTO, y que te la perdonen es un
 * INGRESO: ahí sí cambia lo que tienes. Ese es el único momento en que una
 * deuda entre personas te hace más pobre o más rico.
 *
 * Este módulo es a propósito PURO: no escribe en la base ni crea ids. Recibe
 * objetos planos y devuelve la descripción de lo que hay que hacer. Así se
 * puede probar toda la aritmética sin navegador.
 */

const DIA = 86400000;

export const RESULTADO = {
  PARCIAL: 'parcial',
  EXACTO: 'exacto',
  SOBREPAGO: 'sobrepago',
};

export const DESTINO_SOBRANTE = {
  REGALO: 'regalo',       // "me lo dejó" / "se lo dejé"
  DEVOLVER: 'devolver',   // queda debiendo al revés
};

export const CIERRE = {
  CONDONADA: 'condonada',
  INCOBRABLE: 'incobrable',
};

function soloFecha(x) {
  return x ? String(x).slice(0, 10) : null;
}

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * El orden de los renglones: fecha, luego hora, luego el id. Sin hora cuenta
 * como el comienzo del día. Así, si el préstamo fue a las 9 y el pago a las
 * 3 del mismo día, salen en ese orden.
 */
export function claveOrden(a) {
  return `${a.fecha ?? ''} ${a.hora || '00:00'} ${a.id ?? ''}`;
}

export function diasDesde(fechaIso, hoy = hoyISO()) {
  const f = soloFecha(fechaIso);
  if (!f) return null;
  return Math.round((new Date(hoy + 'T12:00:00') - new Date(f + 'T12:00:00')) / DIA);
}

// ─── Registros de la versión anterior ───────────────────────────────────────
//
// Los préstamos anotados antes de este archivo no tienen `estado` ni
// `fecha_prestamo`. No se les inventa nada: se deduce de lo que sí tienen.

/**
 * El estado de una deuda, aunque sea vieja.
 * Las viejas solo tenían `activa`; del servidor llega como 1/0, del
 * celular como true/false. Una deuda vieja inactiva solo pudo cerrarse
 * pagándose, porque la versión anterior no sabía condonar.
 */
export function estadoDe(d) {
  if (d?.estado) return d.estado;
  return d?.activa === false || d?.activa === 0 ? 'saldada' : 'activa';
}

/**
 * El día en que salió la plata, aunque no se haya guardado.
 *
 * Los ids son UUIDv7: sus primeros 48 bits son el milisegundo en que se
 * crearon. Para un préstamo viejo, esa es la mejor aproximación que hay de
 * cuándo se anotó — y suele ser el mismo día en que se prestó.
 */
export function fechaDePrestamo(d) {
  const guardada = soloFecha(d?.fecha_prestamo) ?? soloFecha(d?.creado_en);
  if (guardada) return guardada;
  const hex = String(d?.id ?? '').replace(/-/g, '').slice(0, 12);
  if (!/^[0-9a-f]{12}$/i.test(hex)) return null;
  const ms = parseInt(hex, 16);
  if (!ms) return null;
  const f = new Date(ms);
  return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`;
}

// ─── Qué va a pasar si registro este abono ──────────────────────────────────

/**
 * Describe el abono ANTES de guardarlo, para poder mostrárselo al usuario.
 *
 * La pantalla usa esto para decir, mientras uno escribe la cifra: "quedaría
 * debiendo $313.000" o "te está pagando $50.000 de más". Que el usuario vea
 * la consecuencia antes de tocar el botón es lo que evita la mitad de los
 * errores de registro.
 */
export function planearAbono(deuda, monto) {
  const saldo = Math.max(0, Math.round(deuda?.saldo_pendiente ?? 0));
  const pago = Math.max(0, Math.round(monto ?? 0));

  const aplicado = Math.min(pago, saldo);
  const sobrante = pago - aplicado;
  const saldoDespues = saldo - aplicado;

  const resultado = sobrante > 0
    ? RESULTADO.SOBREPAGO
    : saldoDespues === 0 && pago > 0
      ? RESULTADO.EXACTO
      : RESULTADO.PARCIAL;

  return {
    resultado,
    saldoAntes: saldo,
    aplicado,          // lo que de verdad tapa deuda
    sobrante,          // lo que se pagó de más
    saldoDespues,
    quedaSaldada: saldoDespues === 0,
    // Solo cuando queda saldo vale la pena preguntar la próxima fecha:
    // pedirla cuando ya quedó saldada es ruido.
    pedirProximaFecha: saldoDespues > 0,
  };
}

/**
 * Los movimientos que hay que crear por un abono.
 *
 * Devuelve una lista de descripciones (sin ids, sin fechas de control) que la
 * pantalla convierte en movimientos reales. Cada una dice de dónde a dónde va
 * la plata y por qué.
 *
 * @param {object} deuda
 * @param {object} op
 * @param {number} op.monto        lo que se pagó, completo
 * @param {string} op.cuentaId     la cuenta real del usuario
 * @param {string} op.espejoId     la cuenta "Me deben" / "Le debo a gente"
 * @param {string} op.fecha
 * @param {string} op.destinoSobrante  qué hacer con lo que sobró
 */
export function movimientosDeAbono(deuda, {
  monto, cuentaId, espejoId, fecha = hoyISO(),
  destinoSobrante = DESTINO_SOBRANTE.REGALO,
  espejoContrarioId = null,
}) {
  const p = planearAbono(deuda, monto);
  const meDeben = Boolean(deuda.me_deben);
  const quien = deuda.acreedor || 'esa persona';
  const movs = [];

  // ── 1. La parte que sí tapa deuda: traslado, nunca gasto ni ingreso ──
  if (p.aplicado > 0) {
    movs.push({
      tipo: 'transferencia',
      monto: p.aplicado,
      fecha,
      concepto: meDeben ? `${quien} te abonó` : `Le abonaste a ${quien}`,
      contraparte: quien,
      // Te deben: la plata sale de "Me deben" y entra a tu cuenta.
      // Tú debes: sale de tu cuenta y entra a "Le debo a gente", cancelándola.
      cuenta_origen: meDeben ? espejoId : cuentaId,
      cuenta_destino: meDeben ? cuentaId : espejoId,
      razon: 'abono',
    });
  }

  // ── 2. Lo que se pagó de más ──
  if (p.sobrante > 0) {
    if (destinoSobrante === DESTINO_SOBRANTE.REGALO) {
      // Nadie lo va a devolver. Entonces sí cambia el patrimonio:
      // si te deben y te pagaron de más, eso es plata que te entró.
      movs.push({
        tipo: meDeben ? 'ingreso' : 'egreso',
        monto: p.sobrante,
        fecha,
        concepto: meDeben
          ? `${quien} te pagó de más y te lo quedaste`
          : `Le pagaste de más a ${quien} y se lo dejaste`,
        contraparte: quien,
        cuenta_origen: meDeben ? null : cuentaId,
        cuenta_destino: meDeben ? cuentaId : null,
        razon: 'sobrepago_regalo',
      });
    } else {
      // Se devuelve: la deuda se da vuelta. Sigue siendo traslado.
      movs.push({
        tipo: 'transferencia',
        monto: p.sobrante,
        fecha,
        concepto: meDeben
          ? `${quien} te pagó de más: ahora le debes`
          : `Le pagaste de más a ${quien}: ahora te debe`,
        contraparte: quien,
        cuenta_origen: meDeben ? espejoContrarioId : cuentaId,
        cuenta_destino: meDeben ? cuentaId : espejoContrarioId,
        razon: 'sobrepago_devolver',
      });
    }
  }

  return { plan: p, movimientos: movs };
}

// ─── Cerrar sin cobrar ──────────────────────────────────────────────────────

/**
 * Cerrar una deuda dejándola así.
 *
 * ── EL ÚNICO MOMENTO EN QUE PRESTAR SÍ TE EMPOBRECE ─────────────────────────
 *
 * Mientras la deuda está viva, la plata sigue siendo tuya: está por fuera,
 * pero es tuya. El día que uno dice "déjalo así", deja de serlo. Ese día —
 * y solo ese — se registra un gasto, y el patrimonio baja de verdad.
 *
 * Al revés es igual de real: el día que a uno le perdonan una deuda, le entró
 * plata. No la puede tocar, pero dejó de deberla, y eso vale exactamente lo
 * mismo que si se la hubieran dado.
 *
 * Por eso esto no es "marcar como inactiva": es un movimiento con su fecha,
 * que aparece en el informe del mes y que uno puede volver a mirar el año que
 * viene para acordarse de a quién no volverle a prestar.
 */
export function movimientosDeCierre(deuda, { espejoId, fecha = hoyISO(), clase = CIERRE.CONDONADA }) {
  const saldo = Math.max(0, Math.round(deuda?.saldo_pendiente ?? 0));
  if (saldo <= 0) return { saldo: 0, movimientos: [] };

  const meDeben = Boolean(deuda.me_deben);
  const quien = deuda.acreedor || 'esa persona';

  return {
    saldo,
    movimientos: [{
      // Te deben y perdonas: sale de "Me deben" y no entra a ninguna parte.
      // Tú debes y te perdonan: entra a "Le debo a gente" y la deja en cero.
      tipo: meDeben ? 'egreso' : 'ingreso',
      monto: saldo,
      fecha,
      concepto: meDeben
        ? (clase === CIERRE.INCOBRABLE
            ? `Diste por perdido lo de ${quien}`
            : `Le perdonaste ${quien === 'esa persona' ? 'la deuda' : 'la deuda a ' + quien}`)
        : `${quien} te perdonó la deuda`,
      contraparte: quien,
      cuenta_origen: meDeben ? espejoId : null,
      cuenta_destino: meDeben ? null : espejoId,
      razon: clase,
    }],
  };
}

// ─── Leer una deuda ─────────────────────────────────────────────────────────

/**
 * El historial completo, listo para pintar.
 *
 * Empieza por el día que salió la plata y sigue con cada cosa que pasó, en
 * orden. Es la respuesta a "¿qué fue lo que pasó con esta deuda?", que es una
 * pregunta distinta de "¿cuánto falta?".
 */
export function historial(deuda, abonos = []) {
  const meDeben = Boolean(deuda.me_deben);
  const propios = abonos
    .filter((a) => a.deuda_id === deuda.id && !a.borrado_en)
    .sort((a, b) => claveOrden(a).localeCompare(claveOrden(b)));

  // Los saldos se DEDUCEN, no se leen de lo guardado en cada fila: si se
  // corrige un pago viejo, los saldos de todos los que siguen cambian.
  const saldos = Object.fromEntries(recalcular(deuda, propios).filas.map((f) => [f.id, f]));

  const filas = [{
    clase: 'prestamo',
    abono_id: null,
    fecha: fechaDePrestamo(deuda),
    hora: deuda.hora_prestamo ?? null,
    titulo: meDeben ? `Le prestaste a ${deuda.acreedor}` : `${deuda.acreedor} te prestó`,
    monto: deuda.monto_original,
    saldo_despues: deuda.monto_original,
    nota: deuda.notas || '',
  }];

  for (const a of propios) {
    filas.push({
      clase: a.clase,
      abono_id: a.id,
      fecha: a.fecha,
      hora: a.hora ?? null,
      titulo: tituloDeAbono(a, meDeben, deuda.acreedor),
      monto: a.monto,
      saldo_despues: saldos[a.id]?.saldo_despues ?? a.saldo_despues,
      nota: a.nota || '',
      proxima_fecha_pago: a.proxima_fecha_pago,
    });
  }

  return filas;
}

function tituloDeAbono(a, meDeben, quien) {
  switch (a.clase) {
    case 'sobrepago':
      return meDeben ? `${quien} pagó de más` : `Le pagaste de más a ${quien}`;
    case 'condonacion':
      return meDeben ? `Le perdonaste el resto` : `${quien} te perdonó el resto`;
    case 'incobrable':
      return meDeben ? 'Lo diste por perdido' : 'Quedó sin pagar';
    case 'ajuste':
      return meDeben ? 'Abonos anteriores' : 'Pagos anteriores';
    default:
      return meDeben ? `${quien} te abonó` : `Le abonaste a ${quien}`;
  }
}

/**
 * Los números de una deuda, con las palabras con que uno los diría.
 *
 * `frase` es lo que se muestra debajo del nombre en la lista. No es
 * decoración: es la diferencia entre ver "Lorena · $313.000" y ver
 * "Lorena · ya te abonó $200.000, se pasó 12 días".
 */
export function resumen(deuda, abonos = [], hoy = hoyISO()) {
  const propios = abonos.filter((a) => a.deuda_id === deuda.id && !a.borrado_en);
  const estado = estadoDe(deuda);
  const saldo = Math.max(0, Math.round(deuda.saldo_pendiente ?? 0));

  // Lo abonado se deduce del saldo, no de sumar filas de abono: así también
  // sale bien en los préstamos viejos, que se abonaron sin dejar historial.
  // Lo perdonado no cuenta como abonado: esa plata no volvió.
  const abonado = estado === 'saldada'
    ? deuda.monto_original
    : Math.max(0, deuda.monto_original - saldo - (deuda.monto_condonado || 0));

  const cerrada = estado !== 'activa';
  const meDeben = Boolean(deuda.me_deben);

  const vence = soloFecha(deuda.proxima_fecha_pago);
  const diasVencida = vence ? diasDesde(vence, hoy) : null;
  const desdeQueSalio = diasDesde(fechaDePrestamo(deuda), hoy);

  let frase;
  if (estado === 'saldada') {
    frase = meDeben ? 'Te pagó completo' : 'Se la pagaste completa';
  } else if (estado === 'condonada') {
    frase = meDeben
      ? `Le perdonaste ${fmt(deuda.monto_condonado)}`
      : `Te perdonó ${fmt(deuda.monto_condonado)}`;
  } else if (estado === 'incobrable') {
    frase = `Diste por perdidos ${fmt(deuda.monto_condonado)}`;
  } else if (abonado > 0 && vence && diasVencida > 0) {
    frase = `${meDeben ? 'Ya te abonó' : 'Ya le abonaste'} ${fmt(abonado)} · ` +
            `se pasó ${diasVencida} día${diasVencida === 1 ? '' : 's'}`;
  } else if (abonado > 0) {
    frase = meDeben ? `Ya te abonó ${fmt(abonado)}` : `Ya le abonaste ${fmt(abonado)}`;
  } else if (vence && diasVencida > 0) {
    frase = `Se pasó ${diasVencida} día${diasVencida === 1 ? '' : 's'}`;
  } else if (vence && diasVencida === 0) {
    frase = meDeben ? 'Te paga hoy' : 'Le pagas hoy';
  } else if (vence) {
    frase = `${meDeben ? 'Te paga' : 'Le pagas'} en ${-diasVencida} día${diasVencida === -1 ? '' : 's'}`;
  } else if (desdeQueSalio !== null && desdeQueSalio > 0) {
    frase = `Sin fecha acordada · hace ${desdeQueSalio} día${desdeQueSalio === 1 ? '' : 's'}`;
  } else {
    frase = 'Sin fecha acordada';
  }

  return {
    saldo,
    abonado,
    cerrada,
    vencida: Boolean(vence && diasVencida > 0 && saldo > 0 && !cerrada),
    diasVencida,
    desdeQueSalio,
    pagos: propios.length,
    frase,
  };
}

/** Formato corto local, para las frases. Evita importar todo dinero.js aquí. */
function fmt(pesos) {
  return '$' + ' ' + new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 })
    .format(Math.abs(Math.round(pesos || 0)));
}

/** Totales de la pantalla: lo que te deben, lo que debes, lo que se perdió. */
export function totales(deudas = []) {
  const vivas = deudas.filter((d) => !d.borrado_en && estadoDe(d) === 'activa');
  const cerradas = deudas.filter((d) => !d.borrado_en && estadoDe(d) !== 'activa');

  return {
    meDeben: vivas.filter((d) => d.me_deben).reduce((t, d) => t + d.saldo_pendiente, 0),
    debo: vivas.filter((d) => !d.me_deben).reduce((t, d) => t + d.saldo_pendiente, 0),
    perdido: cerradas
      .filter((d) => d.me_deben && ['condonada', 'incobrable'].includes(estadoDe(d)))
      .reduce((t, d) => t + (d.monto_condonado || 0), 0),
    recuperado: deudas
      .filter((d) => !d.borrado_en && d.me_deben && estadoDe(d) === 'saldada')
      .reduce((t, d) => t + d.monto_original, 0),
    vivas,
    cerradas,
  };
}

// ─── Corregir después de guardar ────────────────────────────────────────────
//
// Lo que Esteban pidió con estas palabras: "hay cosas que luego de que guardo
// por primera vez no se quieren arreglar". El préstamo de Lorena fue el
// martes y ella pagó el miércoles, pero quedó anotado el jueves: el pago
// salía ANTES que el préstamo y no había cómo cambiarlo.
//
// La forma de que corregir sea seguro es que el saldo no se vaya sumando y
// restando a mano con cada cambio, sino que se DEDUZCA siempre del historial.
// Así, editar o borrar un pago viejo deja todo bien sin tener que adivinar
// qué más había que tocar.

/**
 * El saldo, el estado y los números de una deuda, deducidos de su historial.
 *
 * Es la única fuente de verdad: se llama después de cualquier cambio (pago
 * nuevo, pago editado, pago borrado, préstamo editado) y lo que devuelve se
 * guarda en la deuda. También devuelve el saldo antes y después de cada fila,
 * porque al editar un pago viejo cambian los saldos de todos los que siguen.
 */
export function recalcular(deuda, abonos = []) {
  const propios = abonos
    .filter((a) => a.deuda_id === deuda.id && !a.borrado_en)
    .sort((a, b) => claveOrden(a).localeCompare(claveOrden(b)));

  let saldo = Math.max(0, Math.round(deuda.monto_original || 0));
  let abonado = 0;
  let condonado = 0;
  let estado = 'activa';
  let cerradaEn = null;
  let proxima = deuda.proxima_fecha_pago ?? null;
  const filas = [];

  for (const a of propios) {
    const antes = saldo;
    if (a.clase === 'condonacion' || a.clase === 'incobrable') {
      condonado += saldo;
      saldo = 0;
      estado = a.clase === 'incobrable' ? 'incobrable' : 'condonada';
      cerradaEn = a.fecha;
    } else {
      // abono, sobrepago y ajuste bajan la deuda igual. Lo que sobra de un
      // sobrepago no es deuda: se anotó aparte, como ingreso o deuda al revés.
      const aplicado = Math.min(Math.round(a.monto || 0), saldo);
      saldo -= aplicado;
      abonado += aplicado;
      if (a.proxima_fecha_pago) proxima = a.proxima_fecha_pago;
      if (saldo === 0 && estado === 'activa') {
        estado = 'saldada';
        cerradaEn = a.fecha;
      }
    }
    filas.push({ id: a.id, saldo_antes: antes, saldo_despues: saldo });
  }

  return {
    saldo_pendiente: saldo,
    total_abonado: abonado,
    monto_condonado: condonado,
    estado,
    activa: estado === 'activa',
    cerrada_en: estado === 'activa' ? null : cerradaEn,
    proxima_fecha_pago: estado === 'activa' ? proxima : null,
    filas,
  };
}

/**
 * Para deudas anotadas con la versión anterior, que abonaba sin dejar
 * historial: el renglón que representa esos abonos viejos.
 *
 * Sin él, recalcular() partiría del monto original y "olvidaría" lo que ya
 * se había pagado. Devuelve null si no hace falta.
 */
export function ajusteDeLegado(deuda, abonos = []) {
  const tieneHistorial = abonos.some((a) => a.deuda_id === deuda.id && !a.borrado_en);
  if (tieneHistorial) return null;
  const yaPagado = Math.round(
    (deuda.monto_original || 0) - (deuda.saldo_pendiente || 0) - (deuda.monto_condonado || 0)
  );
  if (yaPagado <= 0) return null;
  return {
    deuda_id: deuda.id,
    clase: 'ajuste',
    fecha: fechaDePrestamo(deuda),
    monto: yaPagado,
    nota: 'Abonos anotados antes de que existiera el historial.',
  };
}

/**
 * Cuáles movimientos son del préstamo en sí (no de los pagos).
 *
 * Los préstamos nuevos guardan el id en `movimiento_id`. Los viejos no, así
 * que se deduce: es el movimiento de la deuda que no pertenece a ningún pago
 * y no es un 4x1000. Si hay varios, el más antiguo.
 */
export function movimientosDelPrestamo(deuda, movimientos = [], abonos = []) {
  const vivos = movimientos.filter((m) => m.deuda_id === deuda.id && !m.borrado_en);
  let principal = deuda.movimiento_id
    ? vivos.find((m) => m.id === deuda.movimiento_id)
    : null;

  if (!principal) {
    const dePagos = new Set(
      abonos.filter((a) => a.deuda_id === deuda.id).map((a) => a.movimiento_id).filter(Boolean)
    );
    principal = vivos
      .filter((m) => !m.movimiento_padre_id && !m.abono_id && !dePagos.has(m.id))
      .sort((a, b) => a.id.localeCompare(b.id))[0] ?? null;
  }

  const gmf = principal
    ? vivos.filter((m) => m.movimiento_padre_id === principal.id)
    : [];
  return { principal, gmf, todos: principal ? [principal, ...gmf] : [] };
}

/** Los movimientos de UN pago: la transferencia, lo que sobró y su 4x1000. */
export function movimientosDelAbono(abono, movimientos = []) {
  const vivos = movimientos.filter((m) => !m.borrado_en);
  const propios = vivos.filter((m) => m.abono_id === abono.id || m.id === abono.movimiento_id);
  const ids = new Set(propios.map((m) => m.id));
  const hijos = vivos.filter((m) => m.movimiento_padre_id && ids.has(m.movimiento_padre_id) && !ids.has(m.id));
  return [...propios, ...hijos];
}

/**
 * ¿Las fechas tienen sentido? Un pago no puede ser anterior al préstamo.
 *
 * Es justo el error que quedó en los datos de Lorena: préstamo el jueves,
 * pago el miércoles. Devuelve el problema en palabras, o null si está bien.
 */
export function validarFechas(fechaPrestamo, abonos = [], deudaId = null) {
  if (!fechaPrestamo) return null;
  const pagos = abonos
    .filter((a) => !a.borrado_en && (!deudaId || a.deuda_id === deudaId) && a.clase !== 'ajuste')
    .sort((a, b) => a.fecha.localeCompare(b.fecha));
  const primero = pagos[0];
  if (primero && primero.fecha < fechaPrestamo) {
    return `Hay un pago del ${fechaHumana(primero.fecha)}, antes del préstamo ` +
           `(${fechaHumana(fechaPrestamo)}). Una de las dos fechas está mal.`;
  }
  return null;
}

/** "2026-09-16" → "miércoles 16 de septiembre". Así lo dice una persona. */
function fechaHumana(iso) {
  return new Date(iso + 'T12:00:00')
    .toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });
}
